// Notification sound utility
export class NotificationSound {
  private static instance: NotificationSound;
  private audioContext: AudioContext | null = null;
  private isEnabled: boolean = true;
  private volume: number = 0.5;

  private constructor() {
    // Initialize audio context on user interaction
    this.initAudioContext();
  }

  public static getInstance(): NotificationSound {
    if (!NotificationSound.instance) {
      NotificationSound.instance = new NotificationSound();
    }
    return NotificationSound.instance;
  }

  private initAudioContext() {
    // Create audio context on first user interaction
    if (typeof window !== "undefined" && !this.audioContext) {
      try {
        this.audioContext = new (
          window.AudioContext || (window as any).webkitAudioContext
        )();
      } catch (error) {
        console.warn("AudioContext not supported:", error);
      }
    }
  }

  public setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
  }

  public setVolume(volume: number) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  public playNotificationSound() {
    if (!this.isEnabled || !this.audioContext) {
      return;
    }

    try {
      // Resume audio context if suspended (required by some browsers)
      if (this.audioContext.state === "suspended") {
        this.audioContext.resume();
      }

      // Create a simple notification sound using Web Audio API
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      // Connect nodes
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Configure the sound
      oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime); // Start frequency
      oscillator.frequency.setValueAtTime(
        1000,
        this.audioContext.currentTime + 0.1,
      ); // End frequency
      oscillator.type = "sine";

      // Configure volume envelope
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(
        this.volume * 0.3,
        this.audioContext.currentTime + 0.01,
      );
      gainNode.gain.exponentialRampToValueAtTime(
        0.001,
        this.audioContext.currentTime + 0.3,
      );

      // Play the sound
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn("Failed to play notification sound:", error);
    }
  }

  // Alternative method using HTML5 Audio (fallback)
  public playNotificationSoundFallback() {
    if (!this.isEnabled) {
      return;
    }

    try {
      // Create a data URL for a simple beep sound
      const audioData = this.generateBeepSound();
      const audio = new Audio(audioData);
      audio.volume = this.volume;
      audio.play().catch((error) => {
        console.warn("Failed to play notification sound (fallback):", error);
      });
    } catch (error) {
      console.warn("Failed to play notification sound (fallback):", error);
    }
  }

  private generateBeepSound(): string {
    // Generate a simple beep sound using Web Audio API and convert to data URL
    if (!this.audioContext) {
      return "";
    }

    const sampleRate = this.audioContext.sampleRate;
    const duration = 0.3; // 300ms
    const samples = Math.floor(sampleRate * duration);
    const buffer = this.audioContext.createBuffer(1, samples, sampleRate);
    const data = buffer.getChannelData(0);

    // Generate a simple sine wave
    for (let i = 0; i < samples; i++) {
      const t = i / sampleRate;
      const frequency = 800 + 200 * Math.sin(2 * Math.PI * 5 * t); // Frequency sweep
      data[i] = Math.sin(2 * Math.PI * frequency * t) * 0.3 * Math.exp(-t * 5);
    }

    // Convert to WAV format (simplified)
    const wav = this.encodeWAV(buffer);
    return `data:audio/wav;base64,${btoa(String.fromCharCode(...wav))}`;
  }

  private encodeWAV(buffer: AudioBuffer): Uint8Array {
    const length = buffer.length;
    const arrayBuffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(arrayBuffer);

    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, "RIFF");
    view.setUint32(4, 36 + length * 2, true);
    writeString(8, "WAVE");
    writeString(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, buffer.sampleRate, true);
    view.setUint32(28, buffer.sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, "data");
    view.setUint32(40, length * 2, true);

    // Convert float samples to 16-bit PCM
    const channelData = buffer.getChannelData(0);
    let offset = 44;
    for (let i = 0; i < length; i++) {
      const sample = Math.max(-1, Math.min(1, channelData[i]));
      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true,
      );
      offset += 2;
    }

    return new Uint8Array(arrayBuffer);
  }
}

// Export a simple function for easy use
export const playNotificationSound = () => {
  const soundManager = NotificationSound.getInstance();
  soundManager.playNotificationSound();
};

// Export for settings/preferences
export const setNotificationSoundEnabled = (enabled: boolean) => {
  const soundManager = NotificationSound.getInstance();
  soundManager.setEnabled(enabled);
};

export const setNotificationSoundVolume = (volume: number) => {
  const soundManager = NotificationSound.getInstance();
  soundManager.setVolume(volume);
};
